import log, { lazyLog } from './utils/logging';
import { addPlayerToState } from './state';
import { parsePlaceHolder } from './parse';
import { attachEventHandlers, attachCustomCloseButton } from './events';
import { getAdsConfig } from './ads';
import { getArticleData } from './utils/getArticleData';
import getDifferenceBetweenObjects from './utils/getDifferenceBetweenObjects';
import ctsEvents from './ctsEvents';
import { deactivateAlarm } from '../alarm';
import { VIDEO } from './constants';

/**
 * Adds events listerners. Handles autoplay based on ads.
 *
 * @param {{ mediaID: string, playerID: string, containerEl: Element, isTop?: boolean, , isTop?: boolean}} baseParams
 * @param {object} overrides
 * @returns {Promise}
 */
export async function setupPlayer(
  { mediaID, playerID, containerEl, isTop = false, wasYouplay = false },
  overrides = {}
) {
  // Store the parent node here - since we might detach the `containerEl` from the DOM later on
  // duinitialization.
  const parent = containerEl.parentElement;

  return new Promise(async (resolve, reject) => {
    if (!window.jwplayer) {
      return reject(new Error('jwplayer not found in window'));
    }

    if (parent) {
      if (parent.classList.contains('jw-initialized')) {
        return;
      }

      parent.classList.add('jw-initialized');
    }

    let articleData = {};
    try {
      articleData = getArticleData(containerEl);
    } catch (e) {
      log.warn(`Failed to get article data for JW embed with:`, { mediaID, playerID }, e);
    }

    const player = jwplayer(containerEl);
    if (!player) {
      return reject(new Error(`jwplayer failed to initialize with id: ${mediaID}`));
    }

    const { defaults: jwDefaults } = window.jwplayer;
    // Build the ads config from the one supplied by the player defaults, which are set in JW
    // dashboard.
    let advertising;
    try {
      advertising = await getAdsConfig(articleData, {
        wasYouplay,
        autostart: overrides.autostart,
        ...overrides,
      });
    } catch (e) {
      log.warn(`Failed to get ads config for JW embed with:`, { mediaID, playerID }, e);
    }

    /**
     * TODO: The diff is always empty - so we need to add a deep clone of the original config object
     * earlier on. Actually, this whole function body shoyuld be refactored into a more chain-like
     * configuration build-up-pattern, like:
     *
     *    const jwConfig = configMerger(jwDefaults)      // A map-reduce-like-style of building the configration.
     *        .mergeMaybe(buildAdsConfig)
     *        .mergeMaybe({ setTimeEvents: true })
     *        .mergeMaybe(overrides)
     *
     */

    // Augment default config
    const jwConfig = {
      ...jwDefaults,
      // Hard-coded overrides
      advertising: {
        ...jwDefaults.advertising,
        ...advertising,
      },
      setTimeEvents: true,
      // Passed in overrides
      ...overrides,
    };

    lazyLog(() => {
      // Print diff between defaults and final config jwConfig
      const diff = getDifferenceBetweenObjects(jwConfig, jwDefaults);
      return [
        'Difference between jwConfig and defaults is -',
        diff,
        'Final player configuration:',
        jwConfig,
        'Difference:',
        diff,
      ];
    });
    player
      .setup(jwConfig)
      .on('ready', ({ setupTime }) => {
        deactivateAlarm(VIDEO);
        lazyLog(() => `Player ${playerID} for media '${mediaID}' ready in ${setupTime} ms`);
        if (parent) {
          const closeBtn = parent.querySelector('.jw-controls .jw-float-icon.jw-icon.jw-button-color.jw-reset');
          attachCustomCloseButton(closeBtn, player);
        }
        addPlayerToState(player, jwConfig, mediaID, isTop);
        attachEventHandlers(player, mediaID);
        resolve(player);
      })
      .on('setupError', (error) => {
        reject(error);
      });
    ctsEvents.forEach((event) => {
      player.on(event, (data) => {
        const customEvent = new CustomEvent('jwplayer', { detail: { ...data, player, isTop } });
        window.dispatchEvent(customEvent);
      });
    });
  });
}

/**
 * Gather and set config defaults
 *
 * @param {Element} placeholderEl
 * @returns {object} - config
 */
export function gatherConfigs(placeholderEl) {
  const params = parsePlaceHolder(placeholderEl);
  const playerID = params.get('playerId');
  const id = params.get('id');
  const enableMovingThumbnail = params.get('enableMovingThumbnail') === 'true';

  if (!playerID || !id) {
    return;
  }

  const isTop = params.get('isTop') === 'true';

  let floating =
    params.get('floating') && params.get('floating') !== 'default'
      ? {
          floating: {
            dismissible: true,
            mode: params.get('floating'),
          },
        }
      : {};

  const aspectratio = params.get('aspectratio');

  if (aspectratio === '9:16') {
    floating = {
      floating: {
        dismissible: true,
        mode: 'never',
      },
    };
  }

  const autostart =
    params.get('autostart') === 'default' || params.get('autostart') === ''
      ? { autostart: 'default' }
      : {
          autostart: params.get('autostart') === 'true' ? 'viewable' : false,
        };

  const mute =
    params.get('mute') === 'default'
      ? {}
      : {
          mute: params.get('mute') === 'true',
        };

  return {
    id,
    playerID,
    placeholderEl,
    isTop,
    config: {
      aspectratio,
      enableMovingThumbnail,
      // We spread because we dont want to set config that are have default values.
      // They should then instead be picked from the player specific jwplayer config
      ...autostart,
      ...floating,
      ...mute,
    },
  };
}
